import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { EntityActionFactory, EntityCache, EntityOp, HttpUrlGenerator } from "@ngrx/data";
import { Store } from "@ngrx/store";
import { ApiResponse, ListingSummary, RestService } from "@recore-solutions/nstar-lib";
import { Observable, combineLatest, of } from "rxjs";
import { catchError, map, switchMap, take, tap } from "rxjs/operators";
import { listingPermissionSelectors } from "src/app/listing-permission.selectors";
import { NstarDataService } from "src/app/nstar-data.service";
import { environment } from "src/environments/environment";
import { ListingPermission } from "../interfaces/listing-permission";
import { listingPermissionMetadata } from "../metadata/listing-permission.metadata";
import { ListingServiceOffering } from "../interfaces/ListingServiceOffering";
import { listingServiceOffering } from "src/app/listing-service-offering.selectors";

@Injectable({
	providedIn: "root",
})
export class ListingSummaryDataService extends NstarDataService<ListingSummary> {
	constructor(
		protected httpClient: HttpClient,
		protected httpUrlGenerator: HttpUrlGenerator,
		protected restService: RestService,
		protected entityCacheStore: Store<EntityCache>,
		protected entityActionFactory: EntityActionFactory
	) {
		super("ListingSummary", httpClient, httpUrlGenerator, entityCacheStore, entityActionFactory);
	}

	getListingSummaryApiResponse(queryParams?: Record<string, any>): Observable<ListingSummary[]> {
		queryParams = {
			sortString: "Listing_ID",
			pageSize: "1000",
			pageNumber: "1",
			showEmptyResultSets: true,
			...queryParams,
		};
		const params = this.queryParamsToHttpParams(queryParams);
		return this.restService
			.get<ApiResponse<ListingSummary, [ListingPermission, ListingServiceOffering]>>(
				environment.endpoints.listingSummary,
				params
			)
			.pipe(
				map((response) => this.fixApiResponse(response)),
				switchMap((fixedApiResponse) => this.clearOldListingPermissions(fixedApiResponse)),
				tap(this.dispatchRelatedRecordActions),
				map(this.mapResponseToRecords),
				catchError(() => of([]))
			);
	}

	fixApiResponse(
		apiResponse: ApiResponse<ListingSummary, [ListingPermission, ListingServiceOffering]>
	): ApiResponse<ListingSummary, [ListingPermission, ListingServiceOffering]> {
		return {
			...apiResponse,
			related: [
				{
					...apiResponse.related[0],
					recordType: "ListingPermission" as never,
				},
				{
					...apiResponse.related[1],
					recordType: "ListingServiceOffering" as never,
				},
			],
		};
	}

	// Whenever we get one or more ListingSummary records we also get updated ListingPermission records.
	// As a result we want to REMOVE any old ListingPermission records before we ADD the updated ones.
	clearOldListingPermissions(
		response: ApiResponse<ListingSummary, [ListingPermission, ListingServiceOffering]>
	): Observable<ApiResponse<ListingSummary, [ListingPermission, ListingServiceOffering]>> {
		return combineLatest([
			this.entityCacheStore.select(listingPermissionSelectors.selectEntities),
			this.entityCacheStore.select(listingServiceOffering.selectEntities),
		]).pipe(
			take(1), // only do this once (i.e. for the current ListingPermissions)
			tap(([permissions]) => {
				this.dispatchRemoveManyForListingPermissions(response.records, permissions);
			}), // dispatch the appropriate action
			map(() => response) // continue with our original API response
		);
	}

	dispatchRemoveManyForListingPermissions(
		listingSummaries: ListingSummary[],
		listingPermissions: ListingPermission[]
	): void {
		const listingSKs: ListingPermission["listingSK"][] = (listingSummaries || []).map(
			(l) => l.listingSK
		);
		const records = listingPermissions
			.filter((lp) => listingSKs.includes(lp.listingSK))
			.map((lp) => listingPermissionMetadata.selectId(lp));
		const listingPermissionPayload: ApiResponse<ListingPermission> = {
			records: records as any,
			recordType: "ListingPermission" as never,
			metadata: {} as any,
		};
		this.dispatchRelatedRecordActions(
			{ related: [listingPermissionPayload] },
			EntityOp.REMOVE_MANY
		);
	}

	getAll(): Observable<ListingSummary[]> {
		return this.getListingSummaryApiResponse();
	}

	getWithQuery(params: Record<string, any>): Observable<ListingSummary[]> {
		return this.getListingSummaryApiResponse(params);
	}
}
